import React, { useEffect, useRef } from "react";
import "./football-app.css";

/**
 * Horizontally scrolling WAI-ARIA tablist. Left/right/Home/End move focus and
 * selection; the selected tab is scrolled into view so a swipe-scrolled bar
 * never hides where you are.
 */
export default function ScrollTabs({ tabs, value, onChange, label = "Sections", idPrefix = "fa" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current?.querySelector('[aria-selected="true"]');
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ inline: "center", block: "nearest" });
  }, [value]);

  const move = (event, index) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    let next = null;
    if (event.key in keys) next = (index + keys[event.key] + tabs.length) % tabs.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    onChange(tabs[next].id);
    const node = ref.current?.querySelectorAll('[role="tab"]')[next];
    if (node) node.focus();
  };

  return (
    <div className="fa-tabs-wrap">
      <div className="fa-tabs" role="tablist" aria-label={label} ref={ref}>
        {tabs.map((tab, i) => (
          <button
            key={tab.id}
            id={`${idPrefix}-tab-${tab.id}`}
            role="tab"
            type="button"
            className="fa-tab"
            aria-selected={value === tab.id}
            aria-controls={`${idPrefix}-panel-${tab.id}`}
            tabIndex={value === tab.id ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => move(e, i)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Small pill switcher for sub-views inside a tab. */
export function Segmented({ options, value, onChange, label }) {
  if (!options || options.length < 2) return null;
  return (
    <div className="fa-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
